import { Coordinate, Region } from '@usepepper/ui/molecules/map/types';
import { getMetersBetween } from '@/utils/distance';
import { NearbyBusiness } from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { useCallback, useMemo, useRef, useState } from 'react';

const MAX_YELP_RADIUS = 40000;

// Top margin is largest since the card extends upward from the pin.
const SAFE_ZONE_TOP = 0.45;
const SAFE_ZONE_BOTTOM = 0.05;
const SAFE_ZONE_SIDE = 0.2;

export interface SearchAreaLocation {
  latitude: number;
  longitude: number;
  // null = no radius cap (e.g. a selected place); a string sets the cap.
  radius: string | null;
}

// True when the pin's card would overflow the viewport.
const isOutsideSafeZone = (business: NearbyBusiness, region: Region) => {
  const north = region.latitude + region.latitudeDelta * (0.5 - SAFE_ZONE_TOP);
  const south =
    region.latitude - region.latitudeDelta * (0.5 - SAFE_ZONE_BOTTOM);
  const sideMargin = region.longitudeDelta * (0.5 - SAFE_ZONE_SIDE);
  return (
    business.latitude > north ||
    business.latitude < south ||
    Math.abs(business.longitude - region.longitude) > sideMargin
  );
};

/**
 * Map interaction state for the Prospect Finder: business selection,
 * "Search this area" gating, and viewport re-query math.
 */
export const useProspectFinderMapInteractions = () => {
  const [selectedBusiness, setSelectedBusiness] = useState<
    NearbyBusiness | undefined
  >(undefined);
  const [mapChanged, setMapChanged] = useState(false);
  const [searchAreaLocation, setSearchAreaLocation] =
    useState<SearchAreaLocation | null>(null);
  const [panToCoordinate, setPanToCoordinate] = useState<
    Coordinate | undefined
  >(undefined);
  const lastIdleRegion = useRef<Region | null>(null);

  const onPanDrag = useCallback(() => {
    setMapChanged(true);
  }, []);

  const onZoomChange = useCallback(() => {
    setMapChanged(true);
  }, []);

  const onRegionChangeComplete = useCallback((region: Region) => {
    lastIdleRegion.current = region;
  }, []);

  const onMapPress = useCallback(() => {
    setSelectedBusiness(undefined);
  }, []);

  // Shared by pin click and results-row click; recenters the map when the
  // pin is off-screen or too near an edge for its card to fit.
  const onBusinessSelect = useCallback((business: NearbyBusiness) => {
    setSelectedBusiness(business);
    setMapChanged(false);
    const region = lastIdleRegion.current;
    // Pan keeps the current zoom; clearing when safe prevents a stale pan
    // request from re-firing later.
    setPanToCoordinate(
      region && isOutsideSafeZone(business, region)
        ? { latitude: business.latitude, longitude: business.longitude }
        : undefined,
    );
  }, []);

  const onSearchArea = useCallback(() => {
    const region = lastIdleRegion.current;
    if (!region) {
      return;
    }
    // Radius is center-to-top-edge of the viewport, capped at Yelp's max.
    const radius = Math.min(
      getMetersBetween(
        region.latitude,
        region.longitude,
        region.latitude + region.latitudeDelta / 2,
        region.longitude,
      ),
      MAX_YELP_RADIUS,
    );
    setSearchAreaLocation({
      latitude: region.latitude,
      longitude: region.longitude,
      radius: Math.round(radius).toString(),
    });
    setSelectedBusiness(undefined);
    setPanToCoordinate(undefined);
    setMapChanged(false);
  }, []);

  // Close the card on filter change — the selected business may drop out.
  const onFilterChange = useCallback(() => {
    setMapChanged(false);
    setSelectedBusiness(undefined);
  }, []);

  // Snap back to the user/HQ anchor: drop the viewport override.
  const onRecenter = useCallback(() => {
    setMapChanged(false);
    setSearchAreaLocation(null);
    setPanToCoordinate(undefined);
  }, []);

  // A location-search selection: re-query around the place and fly there.
  const selectSearchLocation = useCallback((coords: Coordinate) => {
    setSearchAreaLocation({
      latitude: coords.latitude,
      longitude: coords.longitude,
      radius: null,
    });
    setPanToCoordinate(coords);
    setSelectedBusiness(undefined);
    setMapChanged(false);
  }, []);

  return useMemo(
    () => ({
      // Ref to the latest idle viewport; read at render for bounds biasing.
      lastIdleRegion,
      mapChanged,
      onBusinessSelect,
      onFilterChange,
      onMapPress,
      onPanDrag,
      onRecenter,
      onRegionChangeComplete,
      onSearchArea,
      onZoomChange,
      panToCoordinate,
      searchAreaLocation,
      selectedBusiness,
      selectSearchLocation,
    }),
    [
      mapChanged,
      onBusinessSelect,
      onFilterChange,
      onMapPress,
      onPanDrag,
      onRecenter,
      onRegionChangeComplete,
      onSearchArea,
      onZoomChange,
      panToCoordinate,
      searchAreaLocation,
      selectedBusiness,
      selectSearchLocation,
    ],
  );
};
